// Client Internal Audit — the printed report (Company report "client" / Audit report "clean" / Close-out), drawn ONLY from a frozen
// snapshot (lib/iaReport.js buildSnapshot). Layout follows the CCIS internal audit report (cover → company details,
// auditor, results, remarks, definitions → Annex I in the Excel checklist columns). Printed to PDF by
// netlify/functions/render-ia-report-background.js via /ia-report-print/:token. Logo and stamp come from Settings
// (firm_settings), the same ones the bizSAFE report uses.
//
// A4 pages: cover alone · details page alone · Annex I from a new page · Statement of Applicability (27001) from a
// new page. The Audit report (for the certification body) is the same document — its snapshot simply has no finding
// counts and no Obs / NC marks.
import React from 'react'
import { standardsLabel } from '../lib/service'
import { longDate, slashDate, reportTitle, isFinding, totalFindings } from '../lib/iaReport'

// Same look as the audit plan (IAPlanDocument): light rules, CCIS teal header rows, small grey labels.
const TEAL = '#0e5c5c', INK = '#1d2b2b', MUTED = '#5c6b6a', LINE = '#d6e0df', SOFT = '#f2f7f7'
const FONT = '"Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif'
// Tables sit 1px inside the page edge: with collapsed borders half of the outer line falls outside the table box,
// which is what clipped the right-hand border in print.
const table = { width: 'calc(100% - 2px)', margin: '0 1px', borderCollapse: 'collapse', tableLayout: 'fixed' }
// whiteSpace 'normal': the app's own table style (App.css) makes header cells nowrap, which pushed long headings
// ("Close-out (whom / date)") past the column and off the right edge of the page.
const cell = { border: `1px solid ${LINE}`, padding: '5px 7px', verticalAlign: 'top', fontSize: 10.5, lineHeight: 1.4, color: INK, wordWrap: 'break-word', whiteSpace: 'normal' }
const th = { ...cell, background: TEAL, borderColor: TEAL, color: '#fff', fontWeight: 600, textAlign: 'center', verticalAlign: 'bottom' }
const label = { ...cell, background: SOFT, fontWeight: 600, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, verticalAlign: 'middle' }
const val = { ...cell, fontSize: 11.5 }
const band = { ...cell, fontWeight: 700, background: SOFT, color: TEAL }
const h2 = { fontSize: 14, fontWeight: 700, letterSpacing: '0.02em', color: TEAL, margin: '20px 0 8px', borderBottom: `1px solid ${LINE}`, paddingBottom: 4 }
const listTitle = { fontSize: 13, fontWeight: 700, color: TEAL, textAlign: 'center', marginBottom: 8 }
const NEW_PAGE = { breakBefore: 'page', pageBreakBefore: 'always' }
const KEEP = { pageBreakInside: 'avoid', breakInside: 'avoid' }

const sgTime = d => new Date(d).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })
const mark = r => (isFinding(r) || r === 'N/A') ? r : ''
const markCell = r => <td style={{ ...cell, textAlign: 'center', fontWeight: 700, color: r === 'NC' ? '#a32020' : r === 'Obs' ? '#8a5a00' : MUTED }}>{mark(r)}</td>

function Cover({ s, logos }) {
  const h = s.header
  return (
    <section style={{ breakAfter: 'page', pageBreakAfter: 'always' }}>
      <div style={{ height: 90 }}>{logos?.company && <img src={logos.company} alt="CCIS" style={{ maxHeight: 64, maxWidth: 220, objectFit: 'contain' }} />}</div>
      <div style={{ marginTop: '55mm' }}>
        <div style={{ fontSize: 28, fontWeight: 700, color: TEAL, lineHeight: 1.25, letterSpacing: '0.01em' }}>{reportTitle(h.standards)}</div>
        {s.kind === 'closeout' && <div style={{ fontSize: 18, fontWeight: 700, color: TEAL, marginTop: 6 }}>CLOSE-OUT REPORT</div>}
        <div style={{ height: 4, width: 120, background: TEAL, margin: '18px 0' }} />
        <div style={{ fontSize: 20, fontWeight: 600, color: INK }}>{h.company}</div>
        <div style={{ fontSize: 13, marginTop: 18, lineHeight: 1.8, color: MUTED }}>
          <div>Issued On: {slashDate(s.issuedOn)}</div>
          <div>Ref No.: {h.refNo || '—'}</div>
        </div>
      </div>
    </section>
  )
}

function Details({ s, logos }) {
  const h = s.header
  const c = s.counts
  const cols = <colgroup><col style={{ width: '27%' }} /><col style={{ width: '33%' }} /><col style={{ width: '18%' }} /><col style={{ width: '22%' }} /></colgroup>
  return (
    <section>
      <div style={{ ...h2, marginTop: 0 }}>Auditee Company Details</div>
      <table style={table}>{cols}<tbody>
        <tr><td style={label}>Company Name</td><td style={val} colSpan={3}>{h.company}</td></tr>
        <tr><td style={label}>Contact Address</td><td style={{ ...val, whiteSpace: 'pre-wrap' }} colSpan={3}>{h.address || '—'}</td></tr>
        <tr><td style={label}>Date of Audit</td><td style={val}>{longDate(h.auditDate)}</td><td style={label}>Audit Start Time</td><td style={val}>{h.timeOfAudit || '—'}</td></tr>
        <tr><td style={label}>Location of Audit</td><td style={{ ...val, whiteSpace: 'pre-wrap' }} colSpan={3}>{h.location || '—'}</td></tr>
        <tr><td style={label}>Audit Criteria</td><td style={val} colSpan={3}>{standardsLabel(h.standards)}</td></tr>
        <tr><td style={label}>Name of Management Representative</td><td style={val} colSpan={3}>{h.mgmtRep || '—'}</td></tr>
      </tbody></table>

      <div style={h2}>Auditor Information</div>
      <table style={table}>{cols}<tbody>
        <tr><td style={label}>Auditor(s) Name(s)</td><td style={val} colSpan={3}>{(h.auditors || []).join(', ')}</td></tr>
        <tr><td style={label}>Auditing Organisation</td><td style={val} colSpan={3}>CCIS Singapore Pte Ltd</td></tr>
        <tr>
          <td style={label}>Signature of Auditor(s)</td>
          <td style={val}>{(h.signatures || []).map((g, i) => (
            <div key={i} style={{ marginTop: i ? 8 : 0 }}>
              <div>{g.name}</div>
              {g.signed_at && <div style={{ fontSize: 9.5, color: '#666', marginTop: 1 }}>(Electronically signed on {sgTime(g.signed_at)}{g.ip ? ` · IP ${g.ip}` : ''})</div>}
            </div>))}</td>
          <td style={label}>Auditor’s Company Stamp</td>
          <td style={{ ...val, textAlign: 'center' }}>{logos?.stamp && <img src={logos.stamp} alt="Company stamp" style={{ maxHeight: 90, maxWidth: 140, objectFit: 'contain' }} />}</td>
        </tr>
      </tbody></table>

      <div style={h2}>Audit Results &amp; Conclusion</div>
      <p style={{ fontSize: 12, lineHeight: 1.6, margin: 0 }}>{s.results}</p>
      {s.conclusion && <p style={{ fontSize: 12, lineHeight: 1.6, margin: '6px 0 0' }}>{s.conclusion}</p>}
      {s.kind === 'closeout' && totalFindings(c) > 0 &&
        <p style={{ fontSize: 12, lineHeight: 1.6, margin: '6px 0 0', fontWeight: 700 }}>The finding(s) raised in this audit have been closed out as at {longDate(s.closeoutDate)} (see the Close-out column in ANNEX I).</p>}

      <div style={h2}>Remarks</div>
      <ol style={{ fontSize: 12, lineHeight: 1.6, margin: 0, paddingLeft: 22 }}>
        {(s.remarks || []).map((r, i) => <li key={i} style={{ marginBottom: 3 }}>{r}</li>)}
      </ol>

      <div style={h2}>Audit Finding Definitions</div>
      <table style={table}><colgroup><col style={{ width: '32%' }} /><col /></colgroup><tbody>
        {(s.definitions || []).map(([k, v]) => <tr key={k}><td style={label}>{k}</td><td style={val}>{v}</td></tr>)}
      </tbody></table>
    </section>
  )
}

// Annex I columns, as the CCIS Excel checklist: Clause · Requirements · Obs / NC · Audit Findings · Close-out (whom / date).
// The Close-out column prints what the snapshot carries (blank on the Audit report for the certification body).
function MainTable({ list, heading }) {
  const rows = list.rows.filter(r => r.section !== 'soa')
  if (!rows.length) return null
  return (
    <div style={NEW_PAGE}>
      {heading}
      <div style={listTitle}>{list.title}</div>
      <table style={table}>
        <colgroup><col style={{ width: '9%' }} /><col style={{ width: '41%' }} /><col style={{ width: '7%' }} /><col style={{ width: '26%' }} /><col style={{ width: '17%' }} /></colgroup>
        <thead><tr>
          <th style={th}>Clause No</th><th style={th}>{standardsLabel(list.standards)} Requirements</th><th style={th}>Obs / NC</th>
          <th style={th}>Audit Findings / Audit Evidence &amp; Notes</th><th style={th}>Close-out (whom / date)</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => {
            if (r.kind === 'section') return <tr key={i} style={KEEP}><td style={band}>{r.clause_no}</td><td style={band} colSpan={4}>{r.text}</td></tr>
            const bold = r.kind === 'clause'
            return (
              <tr key={i} style={KEEP}>
                {/* Clause headings, and a) rows that carry their own clause number in the Excel (e.g. "4.4.1 a)"). */}
                <td style={{ ...cell, fontWeight: bold ? 700 : 400 }}>{r.clause_no || ''}</td>
                <td style={{ ...cell, fontWeight: bold ? 700 : 400, paddingLeft: r.kind === 'sub' ? 18 : 6, whiteSpace: 'pre-wrap', ...(r.kind === 'note' ? { fontStyle: 'italic', color: '#555' } : {}) }}>
                  {r.kind === 'sub' && r.label ? <span style={{ display: 'inline-block', minWidth: 20, marginLeft: -14 }}>{r.label}</span> : null}{r.text}
                </td>
                {markCell(r.result)}
                <td style={{ ...cell, whiteSpace: 'pre-wrap' }}>{r.notes || ''}</td>
                <td style={{ ...cell, whiteSpace: 'pre-wrap' }}>{r.closeout || ''}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function SoaTable({ list }) {
  const rows = list.rows.filter(r => r.section === 'soa')
  if (!rows.length) return null
  return (
    <div style={NEW_PAGE}>
      <div style={listTitle}>Statement of Applicability</div>
      <table style={table}>
        <colgroup><col style={{ width: '7%' }} /><col style={{ width: '15%' }} /><col style={{ width: '31%' }} /><col style={{ width: '7%' }} /><col style={{ width: '23%' }} /><col style={{ width: '17%' }} /></colgroup>
        <thead><tr>
          <th style={th} colSpan={3}>{standardsLabel(list.standards)} Annex A Requirements</th><th style={th}>Obs / NC</th>
          <th style={th}>Audit Findings / Audit Evidence &amp; Notes</th><th style={th}>Close-out (whom / date)</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => r.kind === 'section'
            ? <tr key={i} style={KEEP}><td style={band} colSpan={6}>{r.clause_no} {r.text}</td></tr>
            : (
              <tr key={i} style={KEEP}>
                <td style={cell}>{r.clause_no}</td><td style={cell}>{r.title}</td><td style={cell}>{r.text}</td>
                {markCell(r.result)}
                <td style={{ ...cell, whiteSpace: 'pre-wrap' }}>{r.notes || ''}</td>
                <td style={{ ...cell, whiteSpace: 'pre-wrap' }}>{r.closeout || ''}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  )
}

export default function IAReportDocument({ snapshot, logos }) {
  const s = snapshot
  if (!s) return null
  const annexHeading = <div style={{ ...h2, marginTop: 0 }}>Annex I – Audit Checklist</div>
  return (
    <div style={{ fontFamily: FONT, color: INK, background: '#fff' }}>
      <Cover s={s} logos={logos} />
      <Details s={s} logos={logos} />
      {s.mode === 'upload'
        ? <div style={NEW_PAGE}>{annexHeading}<p style={{ fontSize: 12 }}>The completed audit checklist is attached on the following pages.</p></div>
        : (s.annex || []).map((l, i) => (
          <React.Fragment key={i}>
            <MainTable list={l} heading={i === 0 ? annexHeading : null} />
            <SoaTable list={l} />
          </React.Fragment>))}
    </div>
  )
}
